import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Brain, Lock, Eye, Calendar } from 'lucide-react';
import { shareApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function SharedChat() {
  const { token } = useParams();
  const { isAuthenticated, user } = useAuthStore();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requiresAuth, setRequiresAuth] = useState(false);

  useEffect(() => {
    loadSharedConversation();
  }, [token, isAuthenticated]);

  const loadSharedConversation = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await shareApi.getSharedConversation(token);
      setData(response.data);
      setRequiresAuth(false);
    } catch (err) {
      if (err.response?.data?.requiresAuth) {
        setRequiresAuth(true);
        setError('This conversation requires you to be logged in to view.');
      } else if (err.response?.status === 404) {
        setError('This share link is invalid or has been revoked.');
      } else if (err.response?.status === 410) {
        setError('This share link has expired.');
      } else {
        setError(err.response?.data?.error || 'Failed to load conversation');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <nav className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/" className="flex items-center space-x-2">
              <Brain className="w-8 h-8 text-primary-600" />
              <span className="text-xl font-bold text-gray-900">ExamPrep</span>
            </Link>
          </div>
        </nav>

        <div className="max-w-lg mx-auto mt-20 p-6 bg-white rounded-xl shadow-sm border text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {requiresAuth ? 'Authentication Required' : 'Unable to Load'}
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>

          {requiresAuth && !isAuthenticated ? (
            <div className="space-y-3">
              <Link
                to="/login"
                className="block w-full py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="block w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Create Account
              </Link>
            </div>
          ) : (
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Go to Homepage
            </Link>
          )}
        </div>
      </div>
    );
  }

  const { conversation, messages, share } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <Brain className="w-8 h-8 text-primary-600" />
              <span className="text-xl font-bold text-gray-900">ExamPrep</span>
            </Link>
            {!isAuthenticated && (
              <div className="flex items-center space-x-3">
                <Link to="/login" className="text-gray-600 hover:text-gray-900">
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Conversation Header */}
        <div className="bg-white rounded-xl border p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {conversation.title || 'Shared Conversation'}
          </h1>
          <div className="flex items-center flex-wrap gap-4 text-sm text-gray-500">
            <div className="flex items-center">
              <span className="mr-2">Shared by</span>
              <span className="font-medium text-gray-700">{conversation.ownerName}</span>
            </div>
            <div className="flex items-center">
              <Eye className="w-4 h-4 mr-1" />
              {share.viewCount} views
            </div>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              {new Date(share.createdAt).toLocaleDateString()}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="p-6 space-y-6">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-5 py-4 ${
                    msg.role === 'user'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  {msg.role === 'user' ? (
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  ) : (
                    <div className="prose prose-sm max-w-none prose-p:my-2 prose-headings:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        {!isAuthenticated && (
          <div className="mt-8 bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-8 text-center text-white">
            <h2 className="text-xl font-bold mb-2">
              Want your own AI ExamPrep coach?
            </h2>
            <p className="text-primary-100 mb-6">
              Start learning memory techniques with personalized AI guidance.
              Free trial available!
            </p>
            <Link
              to="/register"
              className="inline-block px-6 py-3 bg-white text-primary-600 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
            >
              Start Free Trial
            </Link>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t bg-white mt-12 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} ExamPrep. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default SharedChat;
